// src/components/BestBetCard.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { publishExpertPick, getApiErrorMessage } from '../services/communityService';
import './BestBetCard.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const MAIN_MARKETS = ['spreads', 'h2h', 'totals'];
const MARKET_TYPE_LABELS = { spreads: 'Spread', h2h: 'Moneyline', totals: 'Total' };

function isTopPlay(bet) {
  const evPct = parseFloat(bet.expected_value) * 100;
  if (evPct < 3) return false;
  // For player props: require grade A or B
  if (!MAIN_MARKETS.includes(bet.market)) {
    return bet.overall_bet_grade === 'A' || bet.overall_bet_grade === 'B';
  }
  // For main markets: EV alone is sufficient
  return true;
}

// ─── Hit-rate bar ─────────────────────────────────────────────────────────────
function HitBar({ label, pct }) {
  const val = parseFloat(pct);
  if (isNaN(val)) return null;
  const colorClass = val >= 60 ? 'good' : val >= 45 ? 'fair' : 'poor';
  return (
    <div className="bbc-hitbar">
      <span className="bbc-hitbar-label">{label}</span>
      <div className="bbc-hitbar-track">
        <div className={`bbc-hitbar-fill ${colorClass}`} style={{ width: `${Math.min(100, val)}%` }} />
      </div>
      <span className={`bbc-hitbar-pct ${colorClass}`}>{val.toFixed(0)}%</span>
    </div>
  );
}

function formatPercent(val, digits = 1) {
  const num = Number(val);
  if (!Number.isFinite(num)) return '–';
  return `${num.toFixed(digits)}%`;
}

// ─── Bookmaker odds comparison ────────────────────────────────────────────────
function buildComparison(bookmakerPrices, bestBookmakerTitle, bookmakerIcons) {
  if (!bookmakerPrices || typeof bookmakerPrices !== 'object') return [];
  return Object.entries(bookmakerPrices)
    .filter(([, price]) => price != null && Number.isFinite(Number(price)))
    .map(([name, price]) => ({
      bookmaker: name,
      price: Number(price),
      logo: bookmakerIcons?.[name] ?? null,
      isBest: name === bestBookmakerTitle,
    }))
    .sort((a, b) => b.price - a.price);
}

function OddsComparisonRow({ bookmakerPrices, bestBookmakerTitle, bookmakerIcons, fmtOdds }) {
  const items = buildComparison(bookmakerPrices, bestBookmakerTitle, bookmakerIcons);
  if (items.length === 0) return null;
  return (
    <div className="bbc-detail-section">
      <div className="bbc-detail-section-title">Odds Comparison</div>
      <div className="bbc-compare-scroll">
        {items.map(({ bookmaker, price, logo, isBest }) => (
          <div key={bookmaker} className={`bbc-compare-item${isBest ? ' bbc-compare-item--best' : ''}`}>
            {logo
              ? <img src={logo} alt={bookmaker} className="bbc-compare-logo" loading="lazy" width={30} height={30} />
              : <div className="bbc-compare-logo-placeholder" />}
            <span className="bbc-compare-name">{bookmaker}</span>
            <span className={`bbc-compare-odds${isBest ? ' bbc-compare-odds--best' : ''}`}>
              {fmtOdds(price)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Admin: publish this bet to the Community "Expert Picks" board ───────────
// Visible only to staff/superusers (fail-open the same way AdminOverrideBanner
// does, since the backend's IsAdminUser check is the real security boundary —
// this is strictly a UX convenience, never the access control itself).
function AdminPublishControl({ canonicalOddsId }) {
  const [open, setOpen] = useState(false);
  const [headline, setHeadline] = useState('');
  const [commentary, setCommentary] = useState('');
  const [status, setStatus] = useState('idle'); // idle | saving | done | error
  const [message, setMessage] = useState('');

  const handlePublish = async (e) => {
    e.stopPropagation();
    setStatus('saving');
    setMessage('');
    try {
      await publishExpertPick({ canonicalOddsId, headline, commentary });
      setStatus('done');
      setMessage('Published to Community Expert Picks.');
    } catch (err) {
      setStatus('error');
      setMessage(
        err.response?.status === 409
          ? 'This bet has already been published as an Expert Pick.'
          : getApiErrorMessage(err, 'Failed to publish.')
      );
    }
  };

  return (
    <div className="bbc-detail-section bbc-admin-publish" onClick={e => e.stopPropagation()}>
      <div className="bbc-detail-section-title">Admin: Community</div>
      {!open ? (
        <button type="button" className="bbc-admin-publish-toggle" onClick={() => setOpen(true)}>
          Publish to Expert Picks
        </button>
      ) : (
        <div className="bbc-admin-publish-form">
          <input
            className="bbc-admin-publish-input"
            placeholder="Headline (optional)"
            value={headline}
            onChange={e => setHeadline(e.target.value)}
            maxLength={255}
            disabled={status === 'saving' || status === 'done'}
          />
          <textarea
            className="bbc-admin-publish-textarea"
            placeholder="Analysis / commentary (optional)"
            value={commentary}
            onChange={e => setCommentary(e.target.value)}
            rows={2}
            maxLength={4000}
            disabled={status === 'saving' || status === 'done'}
          />
          <div className="bbc-admin-publish-actions">
            <button
              type="button"
              disabled={status === 'saving' || status === 'done'}
              onClick={handlePublish}
            >
              {status === 'saving' ? 'Publishing…' : status === 'done' ? 'Published ✓' : 'Publish'}
            </button>
            {status !== 'done' && (
              <button type="button" className="bbc-admin-publish-cancel" onClick={() => setOpen(false)}>
                Cancel
              </button>
            )}
          </div>
          {message && (
            <p className={`bbc-admin-publish-message${status === 'error' ? ' bbc-admin-publish-message--error' : ''}`}>
              {message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main card ────────────────────────────────────────────────────────────────
// NOTE: `expanded` and `onToggle` props have been intentionally removed.
// Each card manages its own expand/collapse state via internal useState.
// In the parent, always pass a stable `key` prop (e.g. key={bet.id}),
// never key={index}, to prevent state resets on re-render.
function BestBetCard({
  bet,
  rank,
  bookmakerIcons,
  formatMarket,
  fmtOdds,
  lockedOverlay = null,
}) {
  // ✅ FIX: Each card instance owns its own expanded state.
  // This was already the intent, but the dead `expanded`/`onToggle` props
  // implied external control and caused confusion. They are now removed.
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => setIsExpanded(prev => !prev);
  const { user } = useAuth();
  const isAdmin = Boolean(user) && !(user.is_staff === false && user.is_superuser === false);

  const {
    player,
    team,
    market,
    bet_type,
    point_line,
    expected_value,
    price,
    bookmaker,
    predicted_probability,
    last_5_games_percentage,
    last_10_games_percentage,
    last_20_games_percentage,
    home_team,
    away_team,
    commence_time,
    bookmaker_prices,
    signal_score,
  } = bet;

  const isMainMarket    = MAIN_MARKETS.includes(market);
  const ev              = Number(expected_value);
  const evPct           = ev * 100;
  const hasEv           = Number.isFinite(ev);
  const evPositive      = hasEv && evPct > 0;
  const topPlay         = isTopPlay(bet);
  const marketTypeLabel = MARKET_TYPE_LABELS[market] || null;
  const signalScoreNum  = Number(signal_score);
  const hasSignalScore  = Number.isFinite(signalScoreNum);

  // Determine the correct team name for spread/h2h display.
  // Mirrors GameCard.js matchOutcomeTeam logic:
  //   1. `team` field is the primary source (outcome-specific team)
  //   2. `player` field is the secondary source (GameCard checks both)
  //   3. For spreads missing both: derive from point_line sign
  //      (positive line → away-team row, negative → home-team row)
  const spreadDisplayTeam = (() => {
    if (market !== 'spreads' && market !== 'h2h') return null;
    if (team) return team;
    if (player) return player;
    if (market === 'spreads') {
      const pl = Number(point_line);
      if (Number.isFinite(pl)) return pl > 0 ? away_team : home_team;
    }
    return away_team;
  })();

  const gameTime = commence_time
    ? new Date(commence_time).toLocaleString('en-US', {
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      })
    : null;

  const hasHitRates =
    last_5_games_percentage  != null ||
    last_10_games_percentage != null ||
    last_20_games_percentage != null;

  const handleToggleClick = e => {
    e.stopPropagation();
    toggleExpanded();
  };

  // ── Info-grid computed values ─────────────────────────────────────
  const infoWhoLabel = market === 'totals' ? 'Type' : isMainMarket ? 'Team' : 'Player';
  const infoWho = (() => {
    if (market === 'totals') return 'O/U';
    if (isMainMarket) return spreadDisplayTeam || away_team || '—';
    return player || team || '—';
  })();

  const infoBet = (() => {
    if (market === 'h2h') return 'To Win';
    if (market === 'spreads') {
      const pl = Number(point_line);
      const sign = Number.isFinite(pl) && pl > 0 ? '+' : '';
      return point_line != null && point_line !== '' ? `${sign}${point_line}` : '—';
    }
    const side = bet_type
      ? bet_type.charAt(0).toUpperCase() + bet_type.slice(1).toLowerCase()
      : '';
    const line = point_line != null && point_line !== '' ? ` ${point_line}` : '';
    return `${side}${line}`.trim() || '—';
  })();

  const infoMarket = marketTypeLabel || formatMarket(market) || '—';

  const isLocked = Boolean(
    lockedOverlay?.title &&
    lockedOverlay?.description &&
    lockedOverlay?.ctaLabel &&
    lockedOverlay?.ctaTo
  );

  return (
    <article className={`bbc${topPlay ? ' bbc--top' : ''}${isMainMarket ? ' bbc--main' : ''}${isLocked ? ' bbc--locked' : ''}`}>

      <div className="bbc-body">

        {/* ── Dark header ───────────────────────────────────────────── */}
        <div className="bbc-header">
          {away_team && home_team ? (
            <div className="bbc-header-matchup">
              <span className="bbc-header-team">{away_team}</span>
              <span className="bbc-header-at">@</span>
              <span className="bbc-header-team">{home_team}</span>
            </div>
          ) : (
            <div className="bbc-header-team">{player || team || ''}</div>
          )}
          {gameTime && <div className="bbc-header-time">{gameTime} ET</div>}
        </div>

        {/* ── 3-col info grid ──────────────────────────────────────────── */}
        <div className="bbc-info-grid">
          <div className="bbc-info-col">
            <span className="bbc-info-label">{infoWhoLabel}</span>
            <span className="bbc-info-value">{infoWho}</span>
          </div>
          <div className="bbc-info-col bbc-info-col--center">
            <span className="bbc-info-label">Bet</span>
            <span className="bbc-info-value bbc-info-bet-val">{infoBet}</span>
          </div>
          <div className="bbc-info-col bbc-info-col--right">
            <span className="bbc-info-label">Market</span>
            <span className="bbc-info-value">{infoMarket}</span>
          </div>
        </div>

        {/* ── 3-col stats grid ─────────────────────────────────────────── */}
        {(hasEv || (price && bookmaker) || hasSignalScore) && (
          <div className="bbc-stats-grid">
            {hasEv && (
              <div className="bbc-stat-ev">
                <span className="bbc-stat-label">Exp. Value</span>
                <span className={`bbc-stat-ev-value${evPositive ? ' bbc-stat-ev--pos' : evPct >= -1 ? ' bbc-stat-ev--slight-neg' : ' bbc-stat-ev--neg'}`}>
                  {evPositive ? '+' : ''}{evPct.toFixed(1)}%
                </span>
              </div>
            )}
            {hasSignalScore && (
              <div className="bbc-stat-grade">
                <span className="bbc-stat-label">Bet Grade</span>
                <span className="bbc-stat-grade-value">{signalScoreNum.toFixed(1)}</span>
              </div>
            )}
            {price && bookmaker && (
              <div className="bbc-stat-odds">
                <span className="bbc-stat-label">Best Odds</span>
                <div className="bbc-stat-odds-inner">
                  {bookmakerIcons[bookmaker]
                    ? <img src={bookmakerIcons[bookmaker]} alt={bookmaker} className="bbc-stat-book-logo" loading="lazy" width={30} height={30} />
                    : <span className="bbc-stat-book-name">{bookmaker}</span>
                  }
                  <span className="bbc-stat-odds-val">{fmtOdds(price)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Expand toggle ─────────────────────────────────────────── */}
        {!isLocked && (
          <button type="button" className="bbc-expand-hint" onClick={handleToggleClick}>
            <span className={`bbc-chevron${isExpanded ? ' open' : ''}`}>&#8964;</span>
            <span className="bbc-expand-label">{isExpanded ? 'Less detail' : 'More detail'}</span>
          </button>
        )}

        {/* ── Expanded detail ───────────────────────────────────────── */}
        {!isLocked && isExpanded && (
          <div className="bbc-detail" onClick={e => e.stopPropagation()}>
            {predicted_probability != null && (
              <div className="bbc-detail-row">
                <span className="bbc-detail-label">Win Probability</span>
                <span className="bbc-detail-val bbc-detail-prob">
                  {formatPercent(predicted_probability)}
                </span>
              </div>
            )}

            {hasHitRates && (
              <div className="bbc-detail-section">
                <div className="bbc-detail-section-title">Historical Hit Rates</div>
                <HitBar label="L5"  pct={last_5_games_percentage} />
                <HitBar label="L10" pct={last_10_games_percentage} />
                <HitBar label="L20" pct={last_20_games_percentage} />
              </div>
            )}

            <OddsComparisonRow
              bookmakerPrices={bookmaker_prices}
              bestBookmakerTitle={bookmaker}
              bookmakerIcons={bookmakerIcons}
              fmtOdds={fmtOdds}
            />

            {isAdmin && bet.id != null && <AdminPublishControl canonicalOddsId={bet.id} />}
          </div>
        )}
        </div>

      {isLocked && (
        <div className="bbc-lock-overlay">
          <img
            src={lockedOverlay.iconSrc || '/images/unlock_icon.png'}
            alt={lockedOverlay.iconAlt || 'Unlock'}
            className="bbc-lock-icon"
          />
          <h3 className="bbc-lock-title">{lockedOverlay.title}</h3>
          <p className="bbc-lock-copy">{lockedOverlay.description}</p>
          <Link to={lockedOverlay.ctaTo} className="bbc-lock-link">
            {lockedOverlay.ctaLabel}
          </Link>
        </div>
      )}
    </article>
  );
}

// Memoized: prevents re-rendering every card in a list when unrelated sibling state changes.
export default React.memo(BestBetCard);